import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { mediaUrl } from '@/lib/api';
import { useApiQuery } from '@/hooks/useApiQuery';
import { restaurantService } from '@/services/restaurant.service';
import { useLivePageContent } from '@/hooks/usePageContent';
import { PublicLayout } from '@/components/public/PublicLayout';
import { SilaHero } from '@/components/public/SilaHero';
import { ActiveSearchChips, normText } from '@/components/public/ActiveSearchChips';
import { FavoriteButton } from '@/components/public/FavoriteButton';
import { CetsTrees } from '@/components/CetsTrees';
import { cetsSelfService } from '@/services/cets.service';
import { Seo } from '@/components/Seo';
import { Skeleton } from '@/components/ui/skeleton';
import { MapPin, UtensilsCrossed, ArrowRight, ImageIcon, ChefHat, Calendar, Users } from 'lucide-react';

const HERO = 'https://images.pexels.com/photos/262978/pexels-photo-262978.jpeg?auto=compress&cs=tinysrgb&w=1920';
const SLUG = 'dove-mangiare';
const str = (v) => (typeof v === 'string' || typeof v === 'number' ? String(v) : '');
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }); } catch { return iso; } };

function RestaurantCard({ s, rating }) {
  return (
    <Link to={`/ristorante/${s.owner_id}`} className="group block overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid={`dove-card-${s.owner_id}`}>
      <div className="relative h-52 overflow-hidden bg-muted">
        <FavoriteButton structureId={s.owner_id} structureType="restaurant" className="absolute left-3 top-3 z-10" />
        {rating && <div className="absolute bottom-3 left-3 z-10"><CetsTrees rating={rating} ownerId={s.owner_id} size="sm" showLabel={false} /></div>}
        {s.cover_image ? (
          <img src={mediaUrl(s.cover_image)} alt={s.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full items-center justify-center"><ImageIcon className="h-9 w-9 text-muted-foreground" /></div>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-display text-xl font-semibold text-foreground">{s.name}</h3>
        {s.locality && (
          <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary" data-testid={`dove-card-locality-${s.owner_id}`}>
            <MapPin className="h-3 w-3" />{s.locality}
          </span>
        )}
        {s.tagline && <p className="mt-1 line-clamp-1 text-sm text-muted-foreground">{s.tagline}</p>}
        {s.address && <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground"><MapPin className="h-4 w-4 text-primary" />{s.address}</p>}
        <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
          <span className="flex items-center gap-1.5 text-sm text-foreground"><ChefHat className="h-4 w-4 text-primary" />{s.dishes_count} piatt{s.dishes_count === 1 ? 'o' : 'i'}</span>
          <span className="flex items-center gap-1 text-sm font-medium text-primary">Scopri <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
        </div>
      </div>
    </Link>
  );
}

export default function DoveMangiare() {
  const [params, setParams] = useSearchParams();

  const loc = params.get('loc') || '';
  const date = params.get('date') || '';
  const party = params.get('party') || '';
  const locality = params.get('locality') || '';
  const setLocality = (key) => {
    const next = new URLSearchParams(params);
    if (key) next.set('locality', key); else next.delete('locality');
    setParams(next, { replace: true });
  };

  // Dati via react-query (P2.2): cache condivisa tra pagine + dedup + retry automatico.
  const { data: structures, isError } = useApiQuery(['public', 'restaurants'], () => restaurantService.publicStructures());
  const items = isError ? [] : (structures ?? null);
  const ov = useLivePageContent(SLUG);

  // Rating CETS (batch) per mostrare gli alberi sulle card idonee.
  const [ratings, setRatings] = useState({});
  useEffect(() => {
    const ids = (items || []).map((s) => s.owner_id).filter(Boolean);
    if (!ids.length) return;
    cetsSelfService.publicRatings(ids).then((m) => setRatings(m || {})).catch(() => {});
  }, [items]);

  const localityOptions = useMemo(() => {
    const set = new Map();
    (items || []).forEach((s) => { if (s.locality) set.set(s.locality, (set.get(s.locality) || 0) + 1); });
    return Array.from(set.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [items]);

  const filtered = useMemo(() => {
    let list = items || [];
    if (locality) list = list.filter((s) => (s.locality || '') === locality);
    if (loc) {
      const q = normText(loc);
      list = list.filter((s) => normText([s.name, s.address, s.tagline, s.locality].join(' ')).includes(q));
    }
    return list;
  }, [items, loc, locality]);

  const chips = [];
  if (loc) chips.push({ icon: MapPin, text: loc });
  if (locality) chips.push({ icon: MapPin, text: locality });
  if (date) chips.push({ icon: Calendar, text: fmtDate(date) });
  if (party) chips.push({ icon: Users, text: `${party} copert${party === '1' ? 'o' : 'i'}` });
  const clearFilters = () => setParams({}, { replace: true });

  const heroImage = str(ov.heroImage) || HERO;
  const eyebrow = str(ov.eyebrow) || 'Dove Mangiamo';
  const title = str(ov.title) || 'I sapori della Sila';
  const lead = str(ov.lead) || 'Ristoranti, trattorie e locali selezionati: consulta il menu e prenota il tuo tavolo online.';
  const intro = str(ov.intro);

  return (
    <PublicLayout>
      <Seo title="Dove Mangiamo — Sila Experiences" description={lead} path="/dove-mangiare" image={heroImage} />

      <SilaHero
        image={heroImage}
        badge={eyebrow}
        title={title}
        subtitle={lead}
        icon={UtensilsCrossed}
      />

      {intro && (
        <section className="sila-section pt-12" data-testid="dove-intro">
          <p className="max-w-3xl text-lg leading-relaxed text-muted-foreground">{intro}</p>
        </section>
      )}

      <section className="sila-section py-12">
        <h2 className="font-display text-2xl font-bold tracking-tight text-foreground">I ristoranti</h2>
        <p className="mt-1 text-sm text-muted-foreground">Clicca su un locale per aprirne la vetrina, sfogliare il menu e prenotare un tavolo.</p>

        {localityOptions.length > 1 && (
          <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="dove-localities">
            <span className="mr-1 flex items-center gap-1.5 text-sm font-medium text-muted-foreground"><MapPin className="h-4 w-4 text-primary" /> Località:</span>
            <button type="button" onClick={() => setLocality('')} className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${!locality ? 'border-primary bg-primary/10 font-semibold text-primary' : 'border-border hover:bg-muted'}`} data-testid="dove-locality-all">Tutte</button>
            {localityOptions.map(([name, n]) => (
              <button key={name} type="button" onClick={() => setLocality(locality === name ? '' : name)} className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${locality === name ? 'border-primary bg-primary/10 font-semibold text-primary' : 'border-border hover:bg-muted'}`} data-testid={`dove-locality-${name}`}>
                {name} <span className="text-xs text-muted-foreground">({n})</span>
              </button>
            ))}
          </div>
        )}

        <div className="mt-6">
          <ActiveSearchChips chips={chips} onClear={clearFilters} resultCount={filtered.length} noun="ristoranti" />
          {items === null ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-80 w-full rounded-2xl" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-muted/40 p-10 text-center" data-testid="dove-empty">
              <UtensilsCrossed className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-3 font-medium text-foreground">
                {loc ? `Nessun ristorante trovato per “${loc}”` : 'Nessun ristorante disponibile al momento'}
              </p>
              <p className="text-sm text-muted-foreground">
                {loc ? 'Prova a rimuovere i filtri per vedere tutti i ristoranti.' : 'I nuovi locali appariranno qui non appena pubblicheranno la loro vetrina.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="dove-grid">
              {filtered.map((s) => <RestaurantCard key={s.owner_id} s={s} rating={ratings[s.owner_id]} />)}
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
